import type { Pricelist } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { parseNorceDate } from "../parseNorceDate";
import type { ApplicationFlag } from "~/types/storyblok/storyblok";
import { stripHtml } from "~/layers/base/utils/html";

export const PRICE_LIST_FLAG_ID = "pricelist";
const DEFAULT_PLACEMENT = "top,left";

type PriceListFlagSource = Pick<Pricelist, "Id" | "EndDate" | "Description">;

/**
 * Builds a price list's flag from the Storyblok entry `pricelist-<Id>`,
 * else from `pricelist`.
 * @param appFlags - Flags from Storyblok `layouts/main`
 * @param priceList - The product's price list
 * @param now - Current time in ms, to hide an ended list
 * @returns The flag with the list's description as text, or `undefined` when
 *   no entry matches, the list has ended or there is no text or image
 */
export const resolvePriceListFlag = (
	appFlags: ApplicationFlag[],
	priceList: PriceListFlagSource | null | undefined,
	now: number
): ApplicationFlag | undefined => {
	if (!priceList || parseNorceDate(priceList.EndDate) <= now) return undefined;

	const id = `${PRICE_LIST_FLAG_ID}-${priceList.Id}`;
	const entry =
		appFlags.find((flag) => flag.id === id) ??
		appFlags.find((flag) => flag.id === PRICE_LIST_FLAG_ID);
	if (!entry) return undefined;

	const text = entry.text?.trim() || stripHtml(priceList.Description);
	if (!text && !entry.image?.filename) return undefined;

	return {
		...entry,
		id,
		text,
		placement: entry.placement || DEFAULT_PLACEMENT
	};
};
